import { FC, useState, useEffect, useCallback, useMemo } from "react";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from 'leaflet';
import axios from "axios";
import "leaflet/dist/leaflet.css";
import StayCardH from "components/StayCardH/StayCardH";
import ButtonClose from "shared/ButtonClose/ButtonClose";
import { useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet'; // Import Helmet

import { FrontendData, SearchResult, MenuItem } from "../../types/types";

interface SectionGridHasMapProps {
  query: string | null;
  type: number | null;
  open: string | null | undefined;
}

const SectionGridHasMap: FC<SectionGridHasMapProps> = ({ query, type, open }) => {
  const [frontendData, setFrontendData] = useState<FrontendData | null>(null);
  const [searchResults, setSearchResults] = useState<SearchResult[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [showFullMapFixed, setShowFullMapFixed] = useState(false);
  const [userLocation, setUserLocation] = useState<{ lat: number; long: number } | null>(null);
  const [activeItem, setActiveItem] = useState<number | null>(type);
  const navigate = useNavigate();
  const [openNowFilter, setOpenNowFilter] = useState<boolean>(false);
  const [visibleCount, setVisibleCount] = useState(10);
  const handleShowMore = () => {
    setVisibleCount(prevCount => prevCount + 10);
};
  useEffect(() => {
    if(open == "open_now") {
      setOpenNowFilter(true)
    }
  }, [open])

  const setCookie = (name: string, value: string, expirationDate: Date) => {
    document.cookie = `${name}=${value};expires=${expirationDate.toUTCString()}`;
  };

  const getCookie = useCallback((name: string): string | null => {
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const trimmedCookie = cookie.trim();
      if (trimmedCookie.startsWith(`${name}=`)) {
        return trimmedCookie.substring(name.length + 1);
      }
    }
    return null;
  }, []);

  useEffect(() => {
    const fetchFrontendData = () => {
      const frontendDataCookie = getCookie('frontendData');
      if (frontendDataCookie) {
        try {
          const parsedFrontendData: FrontendData = JSON.parse(frontendDataCookie);
          setFrontendData(parsedFrontendData);
        } catch (error) {
          console.error('Error parsing frontendData cookie:', error);
        }
      } else {
        const frontendData = {
          business_count: 87,
          city_id: 1168,
          city_name: "Mumbai",
        };
        const expirationDate = new Date();
        expirationDate.setDate(expirationDate.getDate() + 3);
        setCookie('frontendData', JSON.stringify(frontendData), expirationDate);
        window.location.reload();
      }
    };

    fetchFrontendData();
  }, [getCookie]);

  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({ lat: position.coords.latitude, long: position.coords.longitude });
        },
        (error) => {
          console.log("Error getting user's location:", error.message);
        },
        { enableHighAccuracy: true }
      );
    } else {
      console.log("Geolocation is not supported by this browser.");
    }
  }, []);


  const fetchSearchResults = useCallback(async () => {
    setLoading(true);
    try {
      if (frontendData !== null) {
        const response = await axios.post("/api/search/query/send", {
          query,
          city_id: frontendData.city_id,
          type,
        });
        let results = response.data.data;
        if (openNowFilter) {
          results = results.filter((result: SearchResult) => result.status === "open");
        }
        setSearchResults(results);
      }
    } catch (error) {
      console.error('Error fetching search results:', error);
    } finally {
      setLoading(false);
    }
  }, [frontendData, query, type, openNowFilter]);

  useEffect(() => {
    fetchSearchResults();
  }, [fetchSearchResults]);

  const ChangeUrl = useCallback((activeItem: number) => {
    navigate(`/listing?query=${query}&type=${activeItem}`);
    setActiveItem(activeItem);
  }, [navigate, query]);

  const customIcon = useMemo(() => new L.DivIcon({
    className: 'custom-icon',
    html: '<svg xmlns="http://www.w3.org/2000/svg" style="fill: red;" width="24" height="24" viewBox="0 0 24 24"><path d="M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z"/></svg>',
    iconSize: [48, 48],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  }), []);

  const customIconBlack = useMemo(() => new L.DivIcon({
    className: 'custom-icon',
    html: '<svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24"><path d="M12 0c-4.198 0-8 3.403-8 7.602 0 4.198 3.469 9.21 8 16.398 4.531-7.188 8-12.2 8-16.398 0-4.199-3.801-7.602-8-7.602zm0 11c-1.657 0-3-1.343-3-3s1.343-3 3-3 3 1.343 3 3-1.343 3-3 3z" style="fill: black;"/></svg>',
    iconSize: [48, 48],
    iconAnchor: [12, 24],
    popupAnchor: [0, -24],
  }), []);

  const menuItems: MenuItem[] = useMemo(() => [
    { id: 1, name: "SPA" },
    { id: 2, name: "SALON" },
    { id: 3, name: "WELLNESS" },
  ], []);

  const generateKeywords = (city: string | undefined, type: number | null): string => {
    const typeMapping: { [key: number]: string } = {
      1: "SPA",
      2: "SALON",
      3: "WELLNESS",
    };
    const businessType = type ? typeMapping[type] : "businesses";
    return `top ${businessType.toLowerCase()}, best ${businessType.toLowerCase()}, ${businessType.toLowerCase()} in ${city || "your city"}`;
  };

  const menuItemClassName = useCallback((item: MenuItem) => (
    `block !leading-none font-medium whitespace-nowrap px-5 py-2.5 text-sm sm:text-base sm:px-6 sm:py-3 capitalize rounded-full focus:outline-none ${activeItem === null && item.id === 1 || activeItem === item.id ? "bg-secondary-800 text-secondary-50" : ""}`
  ), [activeItem]);


  const handleOpenNowClick = () => {
    setOpenNowFilter(!openNowFilter);
  };

  return (
    <div className="relative flex min-h-screen">
       <Helmet>
        <title>Find Best Salon and Spa Services & Book online appointment India</title>
        <meta name="description" content={`Find and book appointments with the best salons and spas near you. 
Explore a wide range of beauty and wellness services, read customer reviews, and secure your spot online with ease. Enjoy a seamless booking experience with our comprehensive listing platform` } />
        <meta name="keywords" content={`${generateKeywords(frontendData?.city_name, type)} Best salon booking platform India, Top spa booking app India, Wellness center booking India, Salon appointment app India, Spa and salon booking platform near me, Beauty services booking India, Online salon booking India, Salon reservation system India, Spa appointment scheduler India, Wellness booking software India, Free salon management software India, Free spa software India, Best free wellness management software, Free salon scheduling software, Free spa appointment software, Free wellness center management software, Free beauty salon software, Free salon POS system, Free spa booking system, Free wellness center scheduling software, Best salons near me, Top spas near me, Wellness centers near me, Salon services near me, Spa treatments near me, Wellness programs near me, Salon haircuts near me, Spa massages near me, Beauty treatments near me, Wellness consultations near me, Best beauty salons India, Top rated spas India, Best wellness centers India, Salon booking app free, Free spa booking app, Wellness management software India, Salon appointment scheduling India, Spa reservation app India, Salon booking system near me, Best beauty services India, Free salon booking system, Best salon POS software India, Free spa appointment scheduler, Wellness appointment booking India, Salon and spa booking app, Free beauty booking software, Best salon software free, Free wellness software India, Spa booking platform India, Salon scheduling app India, Free beauty salon scheduler, Best spa booking software free, Top wellness software free, Free salon reservation system, Spa and wellness management software, Salon appointment booking India, Best spa booking platform India, Free salon scheduling system, Wellness reservation app India, Best free salon software India, Top free spa software, Free wellness booking platform, Beauty salon appointment software free, Free spa reservation software, Best salon scheduling software India, Spa appointment booking system India, Wellness booking app India, Free salon and spa software, Best salon management app India, Free spa scheduling software, Wellness booking system India, Salon appointment manager free, Free spa management system, Best wellness booking software India, Free beauty salon management software, Spa appointment manager free, Top wellness booking app India, Free salon management app, Spa booking manager India, Best wellness center software free, Free salon appointment app, Spa booking system near me, Top wellness booking platform India, Free salon management platform, Best spa scheduling system India, Free wellness scheduling app, Salon and spa management software India, Free salon and spa booking app, Best wellness appointment software free, Free spa management app, Top wellness booking system India, Free salon and spa scheduling software, Best beauty booking app India, Free wellness appointment system.`} />
      </Helmet>
      <div className="min-h-screen w-full flex-shrink-0 xl:px-20">
        <div className={`mb-12 mt-24 lg:mb-5 p-5`}>
          <h1 className="text-xl md:text-2xl font-semibold text-left">
            Best {activeItem ? menuItems[activeItem - 1].name.charAt(0).toUpperCase() + menuItems[activeItem - 1].name.slice(1).toLowerCase() : 'venues'} near you in {frontendData?.city_name}
            {query && ` for ${query}`} - Book Online Appointment
          </h1>
          
          {activeItem === 1 && (
            <div className="mt-3">
              <p className="text-sm text-gray-700">
                Discover the best spa and massage centers in {frontendData?.city_name} for a truly relaxing and rejuvenating experience. Whether you need a soothing
                full-body massage, a refreshing facial Massage, aroma therapy, deep tissue massage, or a luxurious spa treatment, these centers offer
                expert therapists and high-quality products to help you unwind.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Easily browse top-rated spa and massage centers in {frontendData?.city_name}, book your appointment online and save time, and take advantage of
                exclusive deals and customer reviews to find the perfect Spa for your wellness and relaxation. Treat yourself to the care you deserve!
              </p>
            
            </div>
          )}
          
          {activeItem === 3 && (
            <div className="mt-3">
              <p className="text-sm text-gray-700">
                Searching for the best wellness center in {frontendData?.city_name}? Enjoy a relaxing and refreshing experience with top-rated wellness centers
                offering a range of holistic services, from yoga therapy and meditation to Ayurveda, naturopathy, stress relief, detox programs, weight
                management, and skincare treatments. Get expert care from certified professionals committed to your health and well-being.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Explore the best wellness centers in {frontendData?.city_name}. Book your appointment online, read customer reviews,
                discover special offers, and check out exclusive wellness packages to start your journey toward a healthier, happier life.
              </p>

            </div>
          )}
          
          {activeItem === 2 && (
            <div className="mt-3">
              <p className="text-sm text-gray-700">
                Find the best salons in {frontendData?.city_name} that offer top-quality hair and beauty services to help you look and feel your best.
                Whether you're looking for a stylish haircut, hair color, bridal makeup, relaxing pedicure, skincare treatments, trendy nail
                art, or smooth waxing, these salons have expert professionals and use high-quality products.
              </p>
              <p className="text-sm text-gray-700 mt-2">
                Easily explore the best salons in {frontendData?.city_name}, book your online appointment, and take advantage of special deals and
                customer reviews to find the perfect Beauty Salon that suits your needs. Give yourself the pampering you deserve!
              </p>
             
            </div>
          )}
          
            <h2 className="flex mt-3 italic weight-bold">
            {loading ? "Loading venues..." :
              searchResults.length > 0 ? (<> <b>{searchResults.length}</b>&nbsp;{activeItem ? (<div className="lowercase"><b>{menuItems[activeItem - 1].name}s</b></div>) : (<>venues</>)} &nbsp;found near you in&nbsp;<b>{frontendData?.city_name}</b></>) :
              `No venues found for your search.`}
            </h2>
        </div>

        <div className="flex items-center justify-between p-2">
          <nav className="nc-Nav relative flex w-full overflow-x-auto text-sm md:text-base hiddenScrollbar">
            <ul className="flex sm:space-x-2">
              {menuItems.map((item) => (
                <li key={item.id} className="nc-NavItem relative">
                  <button
                    className={menuItemClassName(item)}
                    onClick={() => ChangeUrl(item.id)}
                  >
                    {item.name}
                  </button>
                </li>
              ))}
              <li className="nc-NavItem relative">
              <button onClick={handleOpenNowClick} className={`block !leading-none font-medium whitespace-nowrap px-5 py-2.5 text-sm sm:text-base sm:px-6 sm:py-3 capitalize rounded-full focus:outline-none ${openNowFilter ? "bg-red-400 text-white" : "text-gray-800"}`}>
                      OPEN NOW
                  </button>
                </li>
            </ul>
          </nav>
        </div>

        <hr className="pt-4 mt-6 pb-4" />

        <div className="p-5 xl:rounded-[40px] lg:grid lg:grid-cols-1 lg:gap-4">
          <div className="grid xl:grid-cols-2 gap-8">
            {searchResults.slice(0,visibleCount).map((business, index) => (
              <StayCardH
                key={index}
                data={{
                  title: business.business_name,
                  description: business.description,
                  address: business.address,
                  business_type: business.business_type,
                  opening: business.status,
                  galleryImgs: business.gallery,
                  id: business.id,
                  claimed: business.claimed,
                  service_duration: business.service_duration,
                  service_for: business.service_for,
                  service_price: business.service_price,
                  service_name: business.service_name,
                  rating: business.rating,
                  total_rating: business.total_rating,
                  amenities: business.amenities
                }}
                city={frontendData?.city_name}
              />
            ))}
          </div>
          {visibleCount < searchResults.length && (
              <div className="flex items-center justify-center">
              <button
                  onClick={handleShowMore}
                  className="px-4 py-2 bg-blue-500 text-white text-center rounded-3xl"
              >
                  Show More
              </button>
          </div>
            )}
        </div>

        <div className={`${showFullMapFixed ? "fixed inset-0 z-50" : "hidden"}`}>
          {showFullMapFixed && (
            <ButtonClose
              onClick={() => setShowFullMapFixed(false)}
              className="bg-white absolute z-50 left-3 top-3 shadow-lg rounded-xl w-10 h-10"
            />
          )}
          <div className="fixed xl:sticky top-0 left-0 w-full h-full rounded-lg overflow-hidden">
            {(() => {
              const validResults = searchResults.filter(result => 
                result.lat && result.long && 
                !isNaN(result.lat) && !isNaN(result.long) &&
                result.lat !== 0 && result.long !== 0
              );
              
              if (validResults.length === 0) {
                return (
                  <div className="flex items-center justify-center h-full bg-gray-100">
                    <p className="text-gray-600">No map data available for these locations</p>
                  </div>
                );
              }

              const mapCenter: [number, number] = userLocation 
                ? [userLocation.lat, userLocation.long]
                : [validResults[0].lat, validResults[0].long];

              return (
                <MapContainer
                  center={mapCenter}
                  zoom={12}
                  style={{ width: "100%", height: "100%" }}
                >
                  <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" />
                  {userLocation && (
                    <Marker position={[userLocation.lat, userLocation.long]} icon={customIconBlack}>
                      <Popup>
                        <div>
                          <p style={{ margin: 0, fontWeight: 'bold' }}>Your location</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                  {validResults.map((result, index) => (
                    <Marker key={index} position={[result.lat, result.long]} icon={customIcon}>
                      <Popup>
                        <a className="text-black" href={`/venue?id=${result.id}`}>
                          <div>
                            {result.gallery?.[0] && (
                              <img className="rounded-md" width="100" height="50" style={{ margin: 2 }} src={result.gallery[0]} alt={result.business_name} />
                            )}
                            <p style={{ margin: 0, fontWeight: 'bold' }}>{result.business_name}</p>
                            <p style={{ margin: 0 }}>{result.address}</p>
                            {result.service_name && (
                              <>
                                <p style={{ margin: 0, fontWeight: '300' }}>{result.service_name}</p>
                                <p style={{ margin: 0, fontWeight: '300', color: 'green' }}>₹ {result.service_price}</p>
                              </>
                            )}
                            <button style={{ margin: 0, fontWeight: '300', color: 'white', width: "100%", padding: "4px", backgroundColor: 'black' }}>
                              Check Out
                            </button>
                          </div>
                        </a>
                      </Popup>
                    </Marker>
                  ))}
                </MapContainer>
              );
            })()}
          </div>
        </div>

        {!showFullMapFixed && searchResults.length > 0 && 
         searchResults.some(result => result.lat && result.long && !isNaN(result.lat) && !isNaN(result.long) && result.lat !== 0 && result.long !== 0) && (
          <div
            className="flex items-center justify-center fixed bottom-8 left-1/2 transform -translate-x-1/2 px-6 py-2 bg-neutral-900 text-white shadow-2xl rounded-full z-30 space-x-3 text-sm cursor-pointer"
            onClick={() => setShowFullMapFixed(true)}
          >
            <i className="text-lg las la-map"></i>
            <span>Show map</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default SectionGridHasMap;
